import React, { useMemo } from 'react';
import { LayoutDashboard, RotateCcw, Check } from 'lucide-react';
import useDashboardLayout from '../../features/dashboard/useDashboardLayout';
import { widgetsForRole } from '../../features/dashboard/widgets/registry';
import { T, Card, SecondaryButton } from '../../components/em/ui';

// Compact on/off switch built on em tokens (matches the app's toggle look).
const Switch = ({ on, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onChange}
    style={{
      width: 40, height: 22, borderRadius: 9999, border: 0, cursor: 'pointer',
      padding: 2, flexShrink: 0, transition: 'background .15s',
      background: on ? T.orange : T.border,
      display: 'inline-flex', alignItems: 'center',
    }}
  >
    <span
      style={{
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        boxShadow: T.shadowSm, transition: 'transform .15s',
        transform: on ? 'translateX(18px)' : 'translateX(0)',
      }}
    />
  </button>
);

const PersonaliseDashboard = () => {
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const kind = role === 'admin' ? 'admin' : role === 'hr' ? 'hr' : 'member';

  const { hidden, toggleHidden, reset, saving, savedAt } = useDashboardLayout(kind);
  const widgets = useMemo(() => widgetsForRole(kind), [kind]);
  const visibleCount = widgets.filter((w) => !hidden.includes(w.id)).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: T.orange50, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <LayoutDashboard size={20} color={T.orange} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `700 20px ${T.font}`, color: T.fg1, letterSpacing: '-0.02em' }}>
            Personalise Dashboard
          </div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
            Choose which widgets appear on your dashboard. Open <b>Personalise</b> on the
            dashboard to drag, resize, <b>add widgets</b> from the catalogue, and switch
            between <b>saved presets</b>.
          </div>
        </div>
        <span style={{ font: `400 12px ${T.font}`, color: saving ? T.fg5 : T.success, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
          {saving ? 'Saving…' : savedAt ? (<><Check size={13} /> Saved</>) : ''}
        </span>
      </div>

      {/* Widgets list */}
      <Card padding={0} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${T.divider}` }}>
          <span style={{ font: `700 13px ${T.font}`, color: T.fg1 }}>Widgets</span>
          <span style={{ font: `600 11px ${T.font}`, color: T.fg5, padding: '3px 10px', borderRadius: 9999, background: T.bgHead, border: `1px solid ${T.border}` }}>
            {visibleCount}/{widgets.length} visible
          </span>
        </div>

        {widgets.map((w, i) => {
          const isHidden = hidden.includes(w.id);
          const Icon = w.icon;
          return (
            <div
              key={w.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 18px',
                borderTop: i === 0 ? 'none' : `1px solid ${T.divider}`,
              }}
            >
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                background: isHidden ? T.bgHead : T.orange50,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {Icon
                  ? <Icon size={17} color={isHidden ? T.fg7 : T.orange} />
                  : <LayoutDashboard size={17} color={isHidden ? T.fg7 : T.orange} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>{w.title}</div>
                <div style={{ font: `400 11px ${T.font}`, color: T.fg7 }}>{w.id}</div>
              </div>
              <span style={{ font: `500 12px ${T.font}`, color: isHidden ? T.fg7 : T.fg4, minWidth: 48, textAlign: 'right' }}>
                {isHidden ? 'Hidden' : 'Visible'}
              </span>
              <Switch on={!isHidden} onChange={() => toggleHidden(w.id)} />
            </div>
          );
        })}
      </Card>

      {/* Reset */}
      <Card style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ font: `700 13px ${T.font}`, color: T.fg1, marginBottom: 2 }}>Reset layout</div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
            Restore the default layout and make every role-allowed widget visible again.
          </div>
        </div>
        <SecondaryButton icon={RotateCcw} onClick={reset}>
          Reset to default
        </SecondaryButton>
      </Card>
    </div>
  );
};

export default PersonaliseDashboard;
